import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logoImg from "../../../../assets/images/logo-img.png";

// Generic "Download Chart" PDF export, shared by every report. Unlike the old
// per-report google-charts version (which had to redraw a hidden, high-res
// copy of the chart before it could be rasterized), Chart.js already renders
// a real <canvas> on screen, so we can html2canvas-capture the live
// #pdf-content node directly.
export const exportChartPdf = async ({
  elementId,
  title,
  dealerCompany,
  dealerId,
  dataRangeFrom,
  dataRangeTo,
  selectedDiamondValue,
  totalClicks,
  fileName,
}) => {
  const node = document.getElementById(elementId);
  if (!node) return;

  const canvas = await html2canvas(node, {
    useCORS: true,
    scale: 2.5,
    backgroundColor: "#ffffff",
    logging: false,
  });
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 5;
  const headerHeight = 14;
  const footerHeight = 10;
  const infoBlockHeight = 20;
  const availableHeight = pageHeight - headerHeight - footerHeight - infoBlockHeight - 12;

  pdf.setFillColor(31, 38, 62);
  pdf.rect(0, 0, pageWidth, headerHeight, "F");
  try {
    pdf.addImage(logoImg, "PNG", margin, 4, 40, 10);
  } catch {}

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title.toUpperCase(), pageWidth - margin, 11, { align: "right" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  const preparedFor = dealerCompany ? `Prepared For: ${dealerCompany} (${dealerId})` : "Prepared For: -";
  const dateRange = `Date Range: ${moment(dataRangeFrom).format("MMM DD, YYYY")} - ${moment(dataRangeTo).format("MMM DD, YYYY")}`;
  const selectedDiamondLabel =
    selectedDiamondValue === "All" ? "All Diamonds" : selectedDiamondValue === "Mined" ? "Mined Diamonds" : "Lab Grown Diamonds";
  const diamondType = `Selected Diamond: ${selectedDiamondLabel}`;
  const clicks = `Total Clicks: ${(totalClicks || 0).toLocaleString?.() ?? totalClicks ?? 0}`;

  const infoLinesRaw = [preparedFor, dateRange, diamondType, clicks].filter(Boolean);
  const maxWidth = pageWidth - margin * 2;
  const wrappedInfoLines = infoLinesRaw.flatMap((line) => pdf.splitTextToSize(line, maxWidth));
  const lineHeight = 4.8;
  let currentY = headerHeight + 5;
  wrappedInfoLines.forEach((line) => {
    pdf.text(line, margin, currentY);
    currentY += lineHeight;
  });
  currentY += 4;

  const imgRatio = canvas.width / canvas.height;
  let imgWidthMM = pageWidth - margin * 3;
  let imgHeightMM = imgWidthMM / imgRatio;
  if (imgHeightMM > availableHeight) {
    imgHeightMM = availableHeight;
    imgWidthMM = imgHeightMM * imgRatio;
  }
  pdf.addImage(imgData, "PNG", margin, currentY, imgWidthMM, imgHeightMM, undefined, "FAST");

  pdf.setFillColor(31, 38, 62);
  pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  const footerText = `© ${new Date().getFullYear()} JewelCloud Inc. | Generated on ${moment().format("MMM DD, YYYY h:mm A")}`;
  pdf.text(footerText, pageWidth / 2, pageHeight - footerHeight / 2 + 2, { align: "center" });

  const watermarkText = "JewelCloud";
  pdf.setFontSize(90);
  pdf.setFont(undefined, "bolditalic");
  pdf.setTextColor(102, 102, 102);
  pdf.saveGraphicsState();
  pdf.setGState(pdf.GState({ opacity: 0.1 }));
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;
  const textWidth = pdf.getTextWidth(watermarkText);
  const textHeight = pdf.internal.getFontSize() / pdf.internal.scaleFactor;
  const angleRad = (45 * Math.PI) / 180;
  const rotatedWidth = textWidth * Math.cos(angleRad) + textHeight * Math.sin(angleRad);
  const rotatedHeight = textWidth * Math.sin(angleRad) + textHeight * Math.cos(angleRad);
  pdf.text(watermarkText, centerX - rotatedWidth / 2, centerY + rotatedHeight / 4 + 40, { angle: 45 });
  pdf.restoreGraphicsState();

  pdf.save(fileName);
};
